import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

/** Per-conversation composer state; text and ids only, never attachments. */
export interface Draft {
  text: string;
  replyToId: string | null;
  editMessageId: string | null;
  /** What was typed before «Изменить» — restored when editing is cancelled. */
  textBeforeEdit: string | null;
}

const EMPTY: Draft = { text: "", replyToId: null, editMessageId: null, textBeforeEdit: null };

interface DraftsState {
  ownerId: number | null;
  drafts: Record<string, Draft>;
  update: (conversationId: string, patch: Partial<Draft>) => void;
  reset: (conversationId: string) => void;
  claim: (userId: number) => void;
  clear: () => void;
}

const isEmpty = (d: Draft) => !d.text && !d.replyToId && !d.editMessageId;

export const useChatDrafts = create<DraftsState>()(
  persist(
    (set) => ({
      ownerId: null,
      drafts: {},
      update: (conversationId, patch) =>
        set((s) => {
          const next = { ...(s.drafts[conversationId] ?? EMPTY), ...patch };
          const drafts = { ...s.drafts };
          if (isEmpty(next)) delete drafts[conversationId];
          else drafts[conversationId] = next;
          return { drafts };
        }),
      reset: (conversationId) =>
        set((s) => {
          if (!s.drafts[conversationId]) return s;
          const drafts = { ...s.drafts };
          delete drafts[conversationId];
          return { drafts };
        }),
      claim: (userId) => set((s) => (s.ownerId === userId ? s : { ownerId: userId, drafts: s.ownerId === null ? s.drafts : {} })),
      clear: () => set({ ownerId: null, drafts: {} }),
    }),
    {
      name: "turaq-chat-drafts",
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ ownerId: s.ownerId, drafts: s.drafts }),
    },
  ),
);

export const useDraft = (conversationId: string): Draft => useChatDrafts((s) => s.drafts[conversationId] ?? EMPTY);

export const EMPTY_DRAFT = EMPTY;
